import { useEffect } from 'preact/hooks';
import { useAudioRecorder, formatElapsed } from './recordingHooks';
import './record.scss';

interface Props {
  onRecordingComplete: (blob: Blob) => void;
  /** Mirrors the live recording flag so the parent can trim its intro copy
   * while recording (phone frame 3183). */
  onRecordingChange?: (recording: boolean) => void;
}

/**
 * Mic button + live audio-level bar + elapsed timer. Calls
 * `onRecordingComplete` with the audio Blob when the user stops.
 *
 * Styling matches Figma frames 1511/3069 (idle: white mic button on dark
 * card) and 1637/3183 (recording: stop button + red elapsed dot + red
 * level meter on a near-white track). Classes live in record.scss; only
 * the dynamic meter width stays inline.
 */
export function RecorderControls({ onRecordingComplete, onRecordingChange }: Props) {
  const { state, startRecording, stopRecording } = useAudioRecorder(onRecordingComplete);
  const { recording, audioLevel, elapsed, error } = state;

  useEffect(() => {
    onRecordingChange?.(recording);
  }, [recording, onRecordingChange]);

  return (
    <div className="rec-controls">
      {error && <p className="rec-controls__error">{error}</p>}

      {recording ? (
        <>
          <button onClick={stopRecording} aria-label="Stop recording" className="rec-stop-btn">
            {/* Stop square */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
          </button>

          <p className="rec-timer" aria-live="polite">
            <span className="rec-timer__dot" aria-hidden="true" />
            {formatElapsed(elapsed)}
          </p>

          <div
            className="rec-meter"
            role="meter"
            aria-label="Audio level"
            aria-valuenow={Math.round(audioLevel * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="rec-meter__fill" style={{ width: `${audioLevel * 100}%` }} />
          </div>
        </>
      ) : (
        <>
          <button onClick={startRecording} aria-label="Start recording" className="rec-mic-btn">
            {/* Mic icon */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="2" width="6" height="11" rx="3" />
              <path d="M5 10a7 7 0 0 0 14 0" />
              <line x1="12" y1="19" x2="12" y2="22" />
              <line x1="9" y1="22" x2="15" y2="22" />
            </svg>
          </button>
          <p className="rec-tap-label">Tap to record</p>
        </>
      )}
    </div>
  );
}
